import {Injectable} from '@angular/core';
import {GetService} from "@shared/api/services/get.service";
import {AdminUser, AdminDashboardFilters} from "@shared/api/models/query/admin-user.model";
import {Observable} from 'rxjs';
import {PaginationResponse, Pagination} from '@models/pagination.model';


@Injectable({
    providedIn: 'root'
})
export class AdminUsersService extends GetService<AdminUser, AdminUser> {

    protected override get domainEntity() {
        return 'users'
    };


    listAdminUsers(pagination: Pagination = new Pagination(), filters: AdminDashboardFilters = {}): Observable<PaginationResponse<AdminUser>> {
        // Build query params
        const paginationParams = `size=${pagination.size}&start=${pagination.start * pagination.size}`;

        // Always include type=cds
        let queryParams = `type=cdss`;

        // Add sort params
        if (pagination.sort) {
            queryParams += `&sort=${this.mapSortField(pagination.sort.prop)}&order=${pagination.sort.order}`;
        }

        // Add filter params
        queryParams += this.buildFilterParams(filters);

        return this.http.get<PaginationResponse<AdminUser>>(
            `${this.urlToEntity}?${paginationParams}&${queryParams}`
        );
    }

    private buildFilterParams(filters: AdminDashboardFilters): string {
        let params = '';

        // UNIVAPLAT-2574: engagement filter — comma-joined like every other
        // multi-value param (backend splits with explode(',', ...)). Omitted
        // entirely when empty so the unfiltered list is returned.
        if (filters.engagement?.length) {
            params += `&engagement=${filters.engagement.join(',')}`;
        }

        if (filters.role?.length) {
            params += `&role=${filters.role.join(',')}`;
        } else {
            params += `&role=patient,carer`;
        }

        if (filters.category?.length) {
            params += `&category=${filters.category.join(',')}`;
        }

        if (filters.license?.length) {
            params += `&license=${filters.license.join(',')}`;
        }

        if (filters.organisation?.length) {
            params += `&organisation=${filters.organisation.join(',')}`;
        }

        if (filters.service?.length) {
            params += `&service=${filters.service.join(',')}`;
        }

        if (filters.department?.length) {
            params += `&department=${filters.department.join(',')}`;
        }

        if (filters.status?.length) {
            params += `&status=${filters.status.join(',')}`;
        }

        if (filters.careProgramme?.length) {
            params += `&careProgramme=${filters.careProgramme.join(',')}`;
        }

        if (filters.currentWeek?.length) {
            params += `&currentWeek=${filters.currentWeek.join(',')}`;
        }

        if (filters.signupDateStart) {
            params += `&signupDateStart=${filters.signupDateStart}`;
        }

        if (filters.signupDateEnd) {
            params += `&signupDateEnd=${filters.signupDateEnd}`;
        }

        if (filters.firstLoginDateStart) {
            params += `&firstLoginDateStart=${filters.firstLoginDateStart}`;
        }

        if (filters.firstLoginDateEnd) {
            params += `&firstLoginDateEnd=${filters.firstLoginDateEnd}`;
        }

        if (filters.lastLoginDateStart) {
            params += `&lastLoginDateStart=${filters.lastLoginDateStart}`;
        }

        if (filters.lastLoginDateEnd) {
            params += `&lastLoginDateEnd=${filters.lastLoginDateEnd}`;
        }
        params += `&includeDemo=${filters.includeDemo ? 1 : 0}`;

        return params;
    }

    private mapSortField(field: string): string {
        const sortFieldMap: { [key: string]: string } = {
            'createdAt': 'createdAt',
            'firstLoginAt': 'firstLoginAt',
            'lastLoginAt': 'lastLoginAt',
            'firstName': 'firstName',
            'lastName': 'lastName'
        };

        return sortFieldMap[field] || field;
    }
}
